import React from "react";
import { motion } from "framer-motion";
import { useContent, resolveAsset } from "@/context/ContentContext";
import ShareButton from "@/components/ShareButton";

const ease = [0.16, 1, 0.3, 1];

// Slug helper mirrors backend share_cards.slugify
function slugify(text) {
  return String(text || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "card";
}

const DEFAULT_PLATES = [
  { src: "/assets/p-vogue.jpg",       label: "Plate 02 · Vogue",         ratio: "aspect-[3/4]"   },
  { src: "/assets/p-saree-red.jpg",   label: "Plate 03 · Heirloom",      ratio: "aspect-[7/8]"   },
  { src: "/assets/p-marigold.jpg",    label: "Plate 04 · Marigold",      ratio: "aspect-[5/6]"   },
  { src: "/assets/p-purple-top.jpg",  label: "Plate 05 · The Pause",     ratio: "aspect-[1/1]"   },
  { src: "/assets/p-cyber.jpg",       label: "Plate 06 · Neon Hours",    ratio: "aspect-[10/9]"  },
  { src: "/assets/p-quiet-light.jpg", label: "Plate 07 · Quiet Light",   ratio: "aspect-[10/9]"  },
  { src: "/assets/p-green-saree.jpg", label: "Plate 08 · The Verdant",   ratio: "aspect-[10/11]" },
  { src: "/assets/p-purple-sit.jpg",  label: "Plate 09 · The Stillness", ratio: "aspect-[1/1]"   },
  { src: "/assets/p-turtle.jpg",      label: "Plate 10 · Turtleneck",    ratio: "aspect-[10/11]" },
  { src: "/assets/p-dreamy.jpg",      label: "Plate 11 · Hush",          ratio: "aspect-[3/4]"   },
  { src: "/assets/abstract-glass.jpg",label: "Plate 12 · Refraction",    ratio: "aspect-[1/1]"   },
  { src: "/assets/p-dining.jpg",      label: "Plate 13 · Atmosphere",    ratio: "aspect-[3/4]"   },
];

const layout = [
  { col: "md:col-span-5",                  mt: ""           },
  { col: "md:col-span-4",                  mt: "md:mt-20"   },
  { col: "md:col-span-3",                  mt: ""           },
  { col: "md:col-span-4 md:col-start-2",   mt: "md:mt-10"   },
  { col: "md:col-span-3",                  mt: ""           },
  { col: "md:col-span-3",                  mt: "md:mt-16"   },
  { col: "md:col-span-7",                  mt: "md:mt-8"    },
  { col: "md:col-span-3",                  mt: ""           },
  { col: "md:col-span-2",                  mt: "md:mt-20"   },
  { col: "md:col-span-4 md:col-start-3",   mt: ""           },
  { col: "md:col-span-3",                  mt: "md:mt-10"   },
  { col: "md:col-span-2",                  mt: ""           },
];

export default function Gallery() {
  const { content } = useContent() || {};
  const g = content?.gallery || {};
  const plates = g.plates && g.plates.length ? g.plates : DEFAULT_PLATES;
  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative py-24 md:py-40 bg-[#050505]"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-14">
          <div>
            <div className="overline mb-4">
              <span className="accent-bar" />
              {g.kicker || "04 — Faces & Spaces"}
            </div>
            <h2
              className="font-display font-light leading-[0.9] tracking-[-0.03em]"
              style={{ fontSize: "clamp(2.4rem, 7vw, 6.5rem)" }}
            >
              {g.headline_a || "The personal"}{" "}
              <span className="italic text-[#a3a3a3]">{g.headline_b || "archive."}</span>
            </h2>
          </div>
          <p className="overline max-w-xs text-right">
            {g.subtitle || "Twelve plates · curated from a private collection"}
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-12 gap-3 md:gap-5">
          {plates.map((p, i) => {
            const ly = layout[i % layout.length];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 1, ease, delay: (i % 4) * 0.08 }}
                className={`col-span-1 ${ly.col} ${ly.mt}`}
                data-testid={`gallery-item-${i + 1}`}
              >
                <div className={`img-frame relative group ${p.ratio || "aspect-[3/4]"}`}>
                  <img src={resolveAsset(p.src)} alt={p.label} loading="lazy" />
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <ShareButton
                      slug={`plate-${slugify(p.label)}`}
                      label="Share"
                      testId={`share-plate-${i + 1}`}
                    />
                  </div>
                </div>
                <span className="overline mt-2 inline-block">{p.label}</span>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
